import { createSelector } from 'reselect'
import { getIn } from 'immutable'

/** @return {number} 0 if not logged in */
export const selectUserLastFetchTimestamp = state => getIn(state.user, ['timestamp'])

/** @return {?number} (null if not logged in) */
export const selectUserId = state => getIn(state.user, ['userInfo', 'userId'], null)

/** @return {string} (empty string if not logged in)*/
export const selectUserName = state => getIn(state.user, ['userInfo', 'username'], '')

/**
 * @deprecated we should stop storing user email in our DB, meaning it will not
 * be available here in the future. Use selectAlmaTunnusEmail instead.
 * @return {string} (empty string if not logged in)
 */
export const selectUserEmail = state => getIn(state.user, ['userInfo', 'email'], '')

/** @return {string} (empty string if not logged in)*/
export const selectUserHash = state => getIn(state.user, ['userInfo', 'userHash'], '')

/** @return {string} (empty string if not logged in)*/
export const selectUserAlmaIdHash = state => getIn(state.user, ['userInfo', 'almaIdHash'], '')

/** @return {?string} (null if not logged in) */
export const selectUserJWT = state => getIn(state.user, ['userInfo', 'jwt'], null)

/** @return {boolean} */
export const selectIsUserLoggedIn = state => !!selectUserId(state)

/** @return {'logged_in' | 'logged_out'} */
export const selectUserLoginStatusGA4 = state => selectUserId(state) ? 'logged_in' : 'logged_out' // Must be this exact format for our GA4

/** @return {'loggedin' | 'loggedout'} */
export const selectUserLoginStatusADL = state => selectUserId(state) ? 'loggedin' : 'loggedout' // Must be this exact format for AlmaDataLayer

/** @return {Immutable.Map<string,Immutable.Set<number>>} */
export const selectUserCategories = state => getIn(state.user, ['categories'])

/** @return {Immutable.Set<number>} */
export const selectUserBlacklistedCategoryIds = state => getIn(state.user, ['categories', 'blacklisted'])

/** @return {Immutable.Set<number>} */
export const selectUserWhitelistedCategoryIds = state => getIn(state.user, ['categories', 'whitelisted'])

/** @return {Immutable.Map<string,Immutable.Set<number>>} */
export const selectUserTags = state => getIn(state.user, ['tags'])

/** @return {Immutable.Set<number>} */
export const selectUserBlacklistedTagIds = state => getIn(state.user, ['tags', 'blacklisted'])

/** @return {Immutable.Set<number>} */
export const selectUserWhitelistedTagIds = state => getIn(state.user, ['tags', 'whitelisted'])

/** @return {Immutable.Set<number>} */
export const selectUserBlacklistedSourceIds = state => getIn(state.user, ['blacklistedSources'])

/** @return {Immutable.Set<number>} */
export const selectUserSubscribedSourceIds = state => getIn(state.user, ['subscribedSources'])

/** @return {number} negative integer or 0 */
export const selectUserVoteFilterThreshold = state => getIn(state.user, ['voteFilterThreshold'], 0)

/** @return {'default' | 'classic' | 'night' | 'matrix'} */
export const selectUserPrimaryTheme = state => getIn(state.user, ['theme'])

/** @return {'normal' | 'wide' | 'full'} */
export const selectUserLayoutWidth = state => getIn(state.user, ['layoutWidth'])

/** @return {'/' | '/omat' | '/uusimmat' | '/luetuimmat' | '/aanestetyimmat'} */
export const selectUserFrontPage = state => getIn(state.user, ['frontPage'])

/** @return {boolean} */
export const selectIsNightModeEnabled = state => getIn(state.user, ['nightMode'])

/** @return {boolean} */
export const selectUserOverlayIsOpen = state => getIn(state.user, ['overlayIsOpen'])

/** @return {number} */
export const selectUserDefaultWeatherLocationId = state => getIn(state.user, ['defaultLocation'])

/** @return {Immutable.OrderedSet<Immutable.Map>} */
export const selectUserWeatherLocations = state => getIn(state.user, ['locations'])

/** @return {Immutable.OrderedSet<string>} */
export const selectUserWordFilters = state => getIn(state.user, ['wordFilters'])

/** @return {Immutable.OrderedSet<string>} */
export const selectUserSavedSearches = state => getIn(state.user, ['savedSearches'])

/** @return {Immutable.OrderedSet<CustomFeedRecord>} */
export const selectUserCustomFeeds = state => getIn(state.user, ['customFeeds'])

/** @return {boolean} */
export const selectShowUserFeedsSidebar = state => {
  return getIn(state.user, ['showUserFeedsBoxInSidebar'])
}

/**
 * @type {(state: object) => Immutable.Set<CategoryRecord>}
 */
export const selectUserWhitelistedCategories = createSelector(
  selectUserWhitelistedCategoryIds,
  state => state.categories.categories,
  (categoryIds, categoriesMap) => {
    return categoriesMap.filter(cat => categoryIds.includes(cat.id)).toSet()
  }
)

/**
 * @type {(state: object) => Immutable.Set<CategoryRecord>}
 */
export const selectUserBlacklistedCategories = createSelector(
  selectUserBlacklistedCategoryIds,
  state => state.categories.categories,
  (categoryIds, categoriesMap) => {
    return categoriesMap.filter(cat => categoryIds.includes(cat.id)).toSet()
  }
)

/**
 * @type {(state: object) => 'default' | 'classic' | 'night' | 'matrix'}
 */
export const selectUserActiveTheme = createSelector(
  selectUserPrimaryTheme,
  selectIsNightModeEnabled,
  (theme, nightMode) => {
    return nightMode === true ? 'night' : theme
  }
)
